import React, { useState, useMemo, useCallback } from 'react';
import {
    Avatar,
    Box,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Paper,
    Divider,
    GlobalStyles,
} from '@mui/material';
import {
    Search as SearchIcon,
    Print as PrintIcon,
    LocalFireDepartment as FireIcon,
    Visibility as WatchIcon,
} from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import {
    BaseballRecruitProfile,
    BattingStats,
    PitchingStats,
    GameLogEntry,
    SeasonStats,
} from 'api/BaseballRecruits';

interface BaseballScoutingReportProps {
    profiles: BaseballRecruitProfile[];
    groupName?: string;
    groupLogo?: string;
}

interface ScoutingNotes {
    hitting: string;
    fielding: string;
    running: string;
    pitching: string;
}

interface HotHitter {
    profile: BaseballRecruitProfile;
    recentAvg: number;
    recentObp: number;
    recentSlg: number;
    recentOps: number;
    recentK: number;
    recentBb: number;
    games: number;
    sparkNote: string;
}

interface HotPitcher {
    profile: BaseballRecruitProfile;
    recentEra: number;
    recentK: number;
    recentIp: number;
    recentW: number;
    recentL: number;
    games: number;
    sparkNote: string;
}

interface WatchPlayer {
    profile: BaseballRecruitProfile;
    reason: string;
    keyStat: string;
    type: 'breakout' | 'underclassman';
}

const RECENT_GAMES_COUNT = 5;
const HOT_AVG_THRESHOLD = 0.333;
const HOT_OPS_THRESHOLD = 0.9;
const HOT_ERA_THRESHOLD = 2.5;
const BREAKOUT_OPS_DELTA = 0.15;
const BREAKOUT_ERA_RATIO = 0.85;
const MIN_PITCHERS_TO_WATCH = 4;

function num(val: string | undefined): number {
    if (!val || val === '-') return 0;
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
}

function getPlayerName(p: BaseballRecruitProfile): string {
    if (p.individual?.name) return p.individual.name;
    if (p.college_scraped_data?.player?.first_name)
        return `${p.college_scraped_data.player.first_name} ${p.college_scraped_data.player.last_name}`;
    if (p.player_bio) return `${p.player_bio.first_name} ${p.player_bio.last_name}`;
    return 'Unknown';
}

function getPlayerBio(p: BaseballRecruitProfile) {
    const bio = p.player_bio || p.college_scraped_data?.player || p.summer_league_data?.player;
    return {
        positions: bio?.positions?.join(', ') || '-',
        jersey: bio?.jersey || '-',
        classYear: bio?.class_year || '-',
        height: p.player_bio?.height || '-',
        weight: p.player_bio?.weight || '-',
        school: p.player_bio?.school || '-',
    };
}

function getAllGameLogs(p: BaseballRecruitProfile): GameLogEntry[] {
    const logs: GameLogEntry[] = [];
    for (const source of [p.scraped_data, p.college_scraped_data, p.summer_league_data]) {
        if (!source) continue;
        for (const season of source.seasons_data || []) {
            const entries = season.game_logs?.length ? season.game_logs : season.schedule || [];
            logs.push(...entries);
        }
    }
    return logs;
}

function getLatestSeason(p: BaseballRecruitProfile): SeasonStats | null {
    for (const source of [p.college_scraped_data, p.summer_league_data, p.scraped_data]) {
        if (source?.seasons_data?.length) return source.seasons_data[0];
    }
    return null;
}

function getCareerBatting(p: BaseballRecruitProfile): BattingStats | null {
    for (const source of [p.college_scraped_data, p.summer_league_data, p.scraped_data]) {
        if (source?.career_totals?.batting) return source.career_totals.batting;
    }
    return null;
}

function getCareerPitching(p: BaseballRecruitProfile): PitchingStats | null {
    for (const source of [p.college_scraped_data, p.summer_league_data, p.scraped_data]) {
        if (source?.career_totals?.pitching) return source.career_totals.pitching;
    }
    return null;
}

function computeRecentBatting(logs: GameLogEntry[]): {
    avg: number;
    obp: number;
    slg: number;
    ops: number;
    k: number;
    bb: number;
    games: number;
    lastGame: GameLogEntry | null;
} {
    const battingLogs = logs.filter((g) => g.batting?.AB && num(g.batting.AB) > 0);
    const recent = battingLogs.slice(-RECENT_GAMES_COUNT);
    if (recent.length === 0)
        return { avg: 0, obp: 0, slg: 0, ops: 0, k: 0, bb: 0, games: 0, lastGame: null };

    let ab = 0,
        h = 0,
        bb = 0,
        hbp = 0,
        sf = 0,
        tb = 0,
        k = 0;
    for (const g of recent) {
        const b = g.batting!;
        const gAb = num(b.AB);
        const gH = num(b.H);
        ab += gAb;
        h += gH;
        bb += num(b.BB);
        hbp += num(b.HBP);
        sf += num(b.SF);
        k += num(b.K);
        const doubles = num(b['2B']);
        const triples = num(b['3B']);
        const hr = num(b.HR);
        const singles = gH - doubles - triples - hr;
        tb += singles + doubles * 2 + triples * 3 + hr * 4;
    }

    const pa = ab + bb + hbp + sf;
    const avg = ab > 0 ? h / ab : 0;
    const obp = pa > 0 ? (h + bb + hbp) / pa : 0;
    const slg = ab > 0 ? tb / ab : 0;

    return {
        avg,
        obp,
        slg,
        ops: obp + slg,
        k,
        bb,
        games: recent.length,
        lastGame: recent[recent.length - 1],
    };
}

function computeRecentPitching(logs: GameLogEntry[]): {
    era: number;
    k: number;
    ip: number;
    w: number;
    l: number;
    games: number;
} {
    const pitchingLogs = logs.filter((g) => g.pitching?.IP && num(g.pitching.IP) > 0);
    const recent = pitchingLogs.slice(-RECENT_GAMES_COUNT);
    if (recent.length === 0) return { era: 99, k: 0, ip: 0, w: 0, l: 0, games: 0 };

    let ip = 0,
        er = 0,
        k = 0,
        w = 0,
        l = 0;
    for (const g of recent) {
        const pt = g.pitching!;
        ip += num(pt.IP);
        er += num(pt.ER);
        k += num(pt.K);
        w += num(pt.W);
        l += num(pt.L);
    }

    return { era: ip > 0 ? (er / ip) * 9 : 99, k, ip, w, l, games: recent.length };
}

function buildHitterSparkNote(lastGame: GameLogEntry | null): string {
    if (!lastGame?.batting) return '';
    const b = lastGame.batting;
    const h = num(b.H);
    const ab = num(b.AB);
    const xbh = num(b['2B']) + num(b['3B']) + num(b.HR);
    const parts: string[] = [];
    if (ab > 0) parts.push(`${h}-for-${ab} last game`);
    if (xbh > 0) parts.push(`${xbh} XBH`);
    if (num(b.HR) > 0) parts.push(`${b.HR} HR`);
    if (num(b.RBI) > 0) parts.push(`${b.RBI} RBI`);
    return parts.join(', ');
}

function formatStat3(n: number): string {
    return n.toFixed(3).replace(/^0/, '');
}

const printStyles = (
    <GlobalStyles
        styles={{
            '@media print': {
                'body *': { visibility: 'hidden' },
                '.scouting-print-root, .scouting-print-root *': {
                    visibility: 'visible !important' as any,
                },
                '.scouting-print-root': {
                    position: 'absolute' as const,
                    left: 0,
                    top: 0,
                    width: '100%',
                    background: '#fff !important',
                    color: '#000 !important',
                    padding: '16px !important',
                },
                '.scouting-no-print': { display: 'none !important' },
                '.scouting-player-card': {
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid',
                    breakBefore: 'auto',
                    pageBreakBefore: 'auto',
                    marginBottom: '12px',
                },
                '.scouting-tier-header': {
                    breakInside: 'avoid',
                    breakAfter: 'avoid',
                    pageBreakInside: 'avoid',
                    pageBreakAfter: 'avoid',
                },
                '.MuiDialog-root, .MuiBackdrop-root, .MuiDialogTitle-root': {
                    display: 'contents !important' as any,
                    background: 'none !important',
                },
                '.MuiDialogContent-root': {
                    overflow: 'visible !important' as any,
                    maxHeight: 'none !important',
                    padding: '0 !important',
                    border: 'none !important',
                },
                '.MuiDialog-paper': {
                    boxShadow: 'none !important',
                    maxHeight: 'none !important',
                    overflow: 'visible !important' as any,
                    margin: '0 !important',
                },
                '.MuiTabs-root': { display: 'none !important' },
            },
        }}
    />
);

const BaseballScoutingReport: React.FC<BaseballScoutingReportProps> = ({
    profiles,
    groupName,
    groupLogo,
}) => {
    const theme = useTheme();
    const [filterText, setFilterText] = useState('');
    const [notes, setNotes] = useState<Record<string, ScoutingNotes>>({});

    const profilesWithData = useMemo(
        () =>
            profiles.filter(
                (p) => p.scraped_data || p.college_scraped_data || p.summer_league_data,
            ),
        [profiles],
    );

    const filtered = useMemo(() => {
        if (!filterText) return profilesWithData;
        const q = filterText.toLowerCase();
        return profilesWithData.filter((p) => getPlayerName(p).toLowerCase().includes(q));
    }, [profilesWithData, filterText]);

    const hotHitters = useMemo<HotHitter[]>(() => {
        const results: HotHitter[] = [];
        for (const p of profilesWithData) {
            const logs = getAllGameLogs(p);
            const recent = computeRecentBatting(logs);
            if (
                recent.games >= 3 &&
                (recent.avg >= HOT_AVG_THRESHOLD || recent.ops >= HOT_OPS_THRESHOLD)
            ) {
                results.push({
                    profile: p,
                    recentAvg: recent.avg,
                    recentObp: recent.obp,
                    recentSlg: recent.slg,
                    recentOps: recent.ops,
                    recentK: recent.k,
                    recentBb: recent.bb,
                    games: recent.games,
                    sparkNote: buildHitterSparkNote(recent.lastGame),
                });
            }
        }
        results.sort((a, b) => b.recentOps - a.recentOps);
        return results;
    }, [profilesWithData]);

    const hotPitchers = useMemo<HotPitcher[]>(() => {
        const results: HotPitcher[] = [];
        for (const p of profilesWithData) {
            const logs = getAllGameLogs(p);
            const recent = computeRecentPitching(logs);
            if (recent.games >= 2 && recent.era <= HOT_ERA_THRESHOLD) {
                const sparkParts: string[] = [];
                if (recent.ip > 0) sparkParts.push(`${recent.ip.toFixed(1)} IP`);
                if (recent.k > 0) sparkParts.push(`${recent.k} K`);
                results.push({
                    profile: p,
                    recentEra: recent.era,
                    recentK: recent.k,
                    recentIp: recent.ip,
                    recentW: recent.w,
                    recentL: recent.l,
                    games: recent.games,
                    sparkNote: sparkParts.join(', '),
                });
            }
        }
        results.sort((a, b) => a.recentEra - b.recentEra);
        return results;
    }, [profilesWithData]);

    const hittersWhoChase = useMemo(() => {
        const MIN_AB = 8;
        const results: {
            profile: BaseballRecruitProfile;
            kbbRatio: number;
            bat: BattingStats;
        }[] = [];

        for (const p of profilesWithData) {
            const bat = getLatestSeason(p)?.batting;
            if (!bat) continue;
            const ab = num(bat.AB);
            if (ab < MIN_AB) continue;
            const k = num(bat.K);
            const bb = num(bat.BB);
            const hbp = num(bat.HBP);
            const denom = bb + hbp;
            if (denom === 0) continue;
            const ratio = k / denom;
            if (ratio < 2.0) continue;
            results.push({ profile: p, kbbRatio: ratio, bat });
        }

        results.sort((a, b) => b.kbbRatio - a.kbbRatio);
        return results.slice(0, 6);
    }, [profilesWithData]);

    const topRunScorers = useMemo(() => {
        const MIN_AB = 8;
        const results: { profile: BaseballRecruitProfile; rPerAb: number; bat: BattingStats }[] =
            [];
        for (const p of profilesWithData) {
            const bat = getLatestSeason(p)?.batting;
            if (!bat) continue;
            const ab = num(bat.AB);
            if (ab < MIN_AB) continue;
            const r = num(bat.R);
            results.push({ profile: p, rPerAb: r / ab, bat });
        }
        results.sort((a, b) => b.rPerAb - a.rPerAb);
        return results.slice(0, 3);
    }, [profilesWithData]);

    const topRunProducers = useMemo(() => {
        const MIN_AB = 8;
        const results: { profile: BaseballRecruitProfile; rbiPerAb: number; bat: BattingStats }[] =
            [];
        for (const p of profilesWithData) {
            const bat = getLatestSeason(p)?.batting;
            if (!bat) continue;
            const ab = num(bat.AB);
            if (ab < MIN_AB) continue;
            const rbi = num(bat.RBI);
            results.push({ profile: p, rbiPerAb: rbi / ab, bat });
        }
        results.sort((a, b) => b.rbiPerAb - a.rbiPerAb);
        return results.slice(0, 3);
    }, [profilesWithData]);

    const topRunners = useMemo(() => {
        const MIN_AB = 8;
        const results: {
            profile: BaseballRecruitProfile;
            sb: number;
            sba: number;
            bat: BattingStats;
        }[] = [];
        for (const p of profilesWithData) {
            const bat = getLatestSeason(p)?.batting;
            if (!bat) continue;
            const ab = num(bat.AB);
            if (ab < MIN_AB) continue;
            const sb = num(bat.SB);
            if (sb === 0) continue;
            const sba = num(bat['SBA']) || sb;
            results.push({ profile: p, sb, sba, bat });
        }
        results.sort((a, b) => b.sb - a.sb);
        return results.slice(0, 5);
    }, [profilesWithData]);

    const playersToWatch = useMemo<WatchPlayer[]>(() => {
        const results: WatchPlayer[] = [];
        const allOps: { uuid: string; ops: number }[] = [];
        const allEra: { uuid: string; era: number }[] = [];

        for (const p of profilesWithData) {
            const latest = getLatestSeason(p);
            const careerBat = getCareerBatting(p);
            const careerPit = getCareerPitching(p);
            const bio = getPlayerBio(p);

            if (latest?.batting) {
                const currentOps = num(latest.batting.OBP) + num(latest.batting.SLG);
                allOps.push({ uuid: p.uuid, ops: currentOps });

                if (careerBat) {
                    const careerOps = num(careerBat.OBP) + num(careerBat.SLG);
                    if (careerOps > 0 && currentOps - careerOps >= BREAKOUT_OPS_DELTA) {
                        const delta = Math.round((currentOps - careerOps) * 1000);
                        results.push({
                            profile: p,
                            reason: `OPS up ${delta} pts from career avg`,
                            keyStat: `${formatStat3(currentOps)} OPS`,
                            type: 'breakout',
                        });
                    }
                }
            }

            if (latest?.pitching) {
                const currentEra = num(latest.pitching.ERA);
                if (currentEra > 0) allEra.push({ uuid: p.uuid, era: currentEra });

                if (careerPit) {
                    const careerEra = num(careerPit.ERA);
                    if (
                        careerEra > 0 &&
                        currentEra > 0 &&
                        currentEra / careerEra <= BREAKOUT_ERA_RATIO
                    ) {
                        const drop = (careerEra - currentEra).toFixed(2);
                        const alreadyPitcher = results.find(
                            (r) => r.profile.uuid === p.uuid && r.keyStat.includes('ERA'),
                        );
                        if (!alreadyPitcher) {
                            results.push({
                                profile: p,
                                reason: `ERA down ${drop} from career avg`,
                                keyStat: `${currentEra.toFixed(2)} ERA`,
                                type: 'breakout',
                            });
                        }
                    }
                }
            }

            const classLower = bio.classYear.toLowerCase();
            const isUnderclassman =
                classLower.includes('fr') ||
                classLower.includes('so') ||
                classLower.includes('fresh') ||
                classLower.includes('soph') ||
                classLower.includes('9') ||
                classLower.includes('10');

            if (isUnderclassman) {
                const latestSeason = getLatestSeason(p);
                const alreadyHitter = results.find(
                    (r) => r.profile.uuid === p.uuid && r.keyStat.includes('OPS'),
                );
                const alreadyPitcher = results.find(
                    (r) => r.profile.uuid === p.uuid && r.keyStat.includes('ERA'),
                );
                if (!alreadyHitter && latestSeason?.batting) {
                    const ops = num(latestSeason.batting.OBP) + num(latestSeason.batting.SLG);
                    if (ops > 0) {
                        results.push({
                            profile: p,
                            reason: `Underclassman (${bio.classYear})`,
                            keyStat: `${formatStat3(ops)} OPS`,
                            type: 'underclassman',
                        });
                    }
                }
                if (!alreadyPitcher && latestSeason?.pitching) {
                    const era = num(latestSeason.pitching.ERA);
                    if (era > 0) {
                        results.push({
                            profile: p,
                            reason: `Underclassman (${bio.classYear})`,
                            keyStat: `${era.toFixed(2)} ERA`,
                            type: 'underclassman',
                        });
                    }
                }
            }
        }

        // Keep only underclassmen in top 25% by OPS or ERA
        let filtered = results;
        if (allOps.length > 0) {
            allOps.sort((a, b) => b.ops - a.ops);
            const opsThreshold = allOps[Math.floor(allOps.length * 0.25)]?.ops ?? 0;
            filtered = results.filter((r) => {
                if (r.type !== 'underclassman') return true;
                const ops = allOps.find((o) => o.uuid === r.profile.uuid);
                const era = allEra.find((e) => e.uuid === r.profile.uuid);
                if (ops && ops.ops >= opsThreshold) return true;
                if (era) {
                    const sortedEra = [...allEra].sort((a, b) => a.era - b.era);
                    const eraThreshold = sortedEra[Math.floor(sortedEra.length * 0.25)]?.era ?? 99;
                    return era.era <= eraThreshold;
                }
                return false;
            });
        }

        // Backfill: ensure at least MIN_PITCHERS_TO_WATCH pitcher entries
        const pitcherEntries = filtered.filter((r) => r.keyStat.includes('ERA'));
        if (pitcherEntries.length < MIN_PITCHERS_TO_WATCH && allEra.length > 0) {
            const pitcherUuids = new Set(pitcherEntries.map((r) => r.profile.uuid));
            const sortedEra = [...allEra].sort((a, b) => a.era - b.era);
            for (let i = 0; i < sortedEra.length; i++) {
                if (pitcherEntries.length >= MIN_PITCHERS_TO_WATCH) break;
                const entry = sortedEra[i];
                if (pitcherUuids.has(entry.uuid)) continue;
                const profile = profilesWithData.find((p) => p.uuid === entry.uuid);
                if (!profile) continue;
                pitcherUuids.add(entry.uuid);
                const rank = i + 1;
                const suffix = rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th';
                const newEntry: WatchPlayer = {
                    profile,
                    reason: `${rank}${suffix} lowest ERA on roster`,
                    keyStat: `${entry.era.toFixed(2)} ERA`,
                    type: 'breakout',
                };
                filtered.push(newEntry);
                pitcherEntries.push(newEntry);
            }
        }

        return filtered;
    }, [profilesWithData]);

    const orderedSections = useMemo(() => {
        const q = filterText.toLowerCase();
        const matchesFilter = (p: BaseballRecruitProfile) =>
            !q || getPlayerName(p).toLowerCase().includes(q);

        const hotHitterProfiles = hotHitters.map((h) => h.profile).filter(matchesFilter);
        const hotPitcherProfiles = hotPitchers.map((h) => h.profile).filter(matchesFilter);

        const hittersToWatch = playersToWatch
            .filter((w) => w.keyStat.includes('OPS'))
            .map((w) => w.profile)
            .filter(matchesFilter)
            .sort(
                (a, b) =>
                    num(getLatestSeason(b)?.batting?.AVG) - num(getLatestSeason(a)?.batting?.AVG),
            );
        const pitchersToWatch = playersToWatch
            .filter((w) => w.keyStat.includes('ERA'))
            .map((w) => w.profile)
            .filter(matchesFilter);

        const tieredUuids = new Set([
            ...hotHitterProfiles.map((p) => p.uuid),
            ...hotPitcherProfiles.map((p) => p.uuid),
            ...hittersToWatch.map((p) => p.uuid),
            ...pitchersToWatch.map((p) => p.uuid),
        ]);

        const remaining = filtered.filter((p) => !tieredUuids.has(p.uuid));
        const remainingHitters: BaseballRecruitProfile[] = [];
        const remainingPitchers: BaseballRecruitProfile[] = [];
        const noStats: BaseballRecruitProfile[] = [];

        for (const p of remaining) {
            const bat = getCareerBatting(p);
            const pit = getCareerPitching(p);
            if (bat) {
                remainingHitters.push(p);
            } else if (pit) {
                remainingPitchers.push(p);
            } else {
                noStats.push(p);
            }
        }

        remainingHitters.sort(
            (a, b) => num(getCareerBatting(b)?.AVG) - num(getCareerBatting(a)?.AVG),
        );
        remainingPitchers.sort(
            (a, b) => num(getCareerPitching(b)?.IP) - num(getCareerPitching(a)?.IP),
        );
        noStats.sort((a, b) => getPlayerName(a).localeCompare(getPlayerName(b)));

        const sections: { label: string; profiles: BaseballRecruitProfile[] }[] = [];
        if (hotHitterProfiles.length > 0)
            sections.push({ label: 'Hot Hitters', profiles: hotHitterProfiles });
        if (hotPitcherProfiles.length > 0)
            sections.push({ label: 'Hot Pitchers', profiles: hotPitcherProfiles });
        if (hittersToWatch.length > 0)
            sections.push({ label: 'Hitters to Watch', profiles: hittersToWatch });
        if (pitchersToWatch.length > 0)
            sections.push({ label: 'Pitchers to Watch', profiles: pitchersToWatch });
        if (remainingHitters.length > 0)
            sections.push({ label: 'Hitters', profiles: remainingHitters });
        if (remainingPitchers.length > 0)
            sections.push({ label: 'Pitchers', profiles: remainingPitchers });
        if (noStats.length > 0) sections.push({ label: '', profiles: noStats });

        return sections;
    }, [filtered, filterText, hotHitters, hotPitchers, playersToWatch]);

    const updateNote = useCallback((uuid: string, field: keyof ScoutingNotes, value: string) => {
        setNotes((prev) => ({
            ...prev,
            [uuid]: {
                ...(prev[uuid] || { hitting: '', fielding: '', running: '', pitching: '' }),
                [field]: value,
            },
        }));
    }, []);

    const sectionHeader = (label: string, color: string, icon?: React.ReactNode) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 3,
                mb: 1.5,
                px: 1,
                py: 0.75,
                borderLeft: `3px solid ${color}`,
                backgroundColor: `${color}10`,
            }}
        >
            {icon}
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 800,
                    color,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                }}
            >
                {label}
            </Typography>
        </Box>
    );

    const tierHeader = (label: string) => (
        <Box
            className="scouting-tier-header"
            sx={{
                mt: 3,
                mb: 1,
                breakInside: 'avoid',
                breakAfter: 'avoid',
                '@media print': { breakInside: 'avoid', breakAfter: 'avoid' },
            }}
        >
            <Divider />
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    mt: 0.75,
                }}
            >
                {label}
            </Typography>
        </Box>
    );

    const compactStatCell = {
        fontSize: '0.72rem',
        textAlign: 'center' as const,
        py: 0.5,
        px: 0.75,
    };

    const compactHeaderCell = {
        fontWeight: 700,
        fontSize: '0.68rem',
        textAlign: 'center' as const,
        py: 0.5,
        px: 0.75,
        whiteSpace: 'nowrap' as const,
    };

    const renderPlayerCard = (profile: BaseballRecruitProfile) => {
        const bio = getPlayerBio(profile);
        const latest = getLatestSeason(profile);
        const playerNotes = notes[profile.uuid] || {
            hitting: '',
            fielding: '',
            running: '',
            pitching: '',
        };

        return (
            <Paper
                key={profile.uuid}
                className="scouting-player-card"
                variant="outlined"
                sx={{
                    mb: 2,
                    p: 2,
                    borderColor: theme.palette.divider,
                    '@media print': { overflow: 'visible' },
                }}
            >
                {/* Player Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1.5,
                        mb: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {getPlayerName(profile)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        #{bio.jersey}
                    </Typography>
                    <Chip label={bio.positions} size="small" variant="outlined" />
                    <Typography variant="body2" color="text.secondary">
                        {bio.school}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {bio.classYear}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {bio.height} / {bio.weight}
                    </Typography>
                </Box>

                {/* Season Stats Summary */}
                {latest?.batting &&
                    (() => {
                        const batK = num(latest.batting?.K);
                        const batBB = num(latest.batting?.BB);
                        const batHBP = num(latest.batting?.HBP);
                        const kbbDenom = batBB + batHBP;
                        const kbbRatio = kbbDenom > 0 ? batK / kbbDenom : null;
                        const kbbColor =
                            kbbRatio === null
                                ? 'text.secondary'
                                : kbbRatio > 3.0
                                ? '#e65100'
                                : kbbRatio <= 1.5
                                ? '#2e7d32'
                                : 'inherit';
                        return (
                            <TableContainer sx={{ mb: 1.5 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    ...compactHeaderCell,
                                                    textAlign: 'left',
                                                    fontWeight: 800,
                                                }}
                                            >
                                                Batting
                                            </TableCell>
                                            {[
                                                'GP',
                                                'AVG',
                                                'AB',
                                                'R',
                                                'H',
                                                'HR',
                                                'RBI',
                                                'BB',
                                                'K',
                                                'SB',
                                                'OBP',
                                                'SLG',
                                            ].map((col) => (
                                                <TableCell key={col} sx={compactHeaderCell}>
                                                    {col}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                sx={{ ...compactHeaderCell, fontWeight: 800 }}
                                            >
                                                K/(BB+HBP)
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    ...compactStatCell,
                                                    textAlign: 'left',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                }}
                                            >
                                                {latest.year} {latest.team_level}
                                            </TableCell>
                                            {[
                                                'GP',
                                                'AVG',
                                                'AB',
                                                'R',
                                                'H',
                                                'HR',
                                                'RBI',
                                                'BB',
                                                'K',
                                                'SB',
                                                'OBP',
                                                'SLG',
                                            ].map((col) => (
                                                <TableCell key={col} sx={compactStatCell}>
                                                    {latest.batting?.[col] || '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                sx={{
                                                    ...compactStatCell,
                                                    fontWeight: 700,
                                                    color: kbbColor,
                                                }}
                                            >
                                                {kbbRatio !== null ? kbbRatio.toFixed(2) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        );
                    })()}

                {latest?.pitching && (
                    <TableContainer sx={{ mb: 1.5 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...compactHeaderCell,
                                            textAlign: 'left',
                                            fontWeight: 800,
                                        }}
                                    >
                                        Pitching
                                    </TableCell>
                                    {['ERA', 'W', 'L', 'APP', 'GS', 'IP', 'K', 'BB', 'WHIP'].map(
                                        (col) => (
                                            <TableCell key={col} sx={compactHeaderCell}>
                                                {col}
                                            </TableCell>
                                        ),
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...compactStatCell,
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {latest.year} {latest.team_level}
                                    </TableCell>
                                    {['ERA', 'W', 'L', 'APP', 'GS', 'IP', 'K', 'BB', 'WHIP'].map(
                                        (col) => (
                                            <TableCell key={col} sx={compactStatCell}>
                                                {latest.pitching?.[col] || '-'}
                                            </TableCell>
                                        ),
                                    )}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {latest?.fielding && (
                    <TableContainer sx={{ mb: 1.5 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...compactHeaderCell,
                                            textAlign: 'left',
                                            fontWeight: 800,
                                        }}
                                    >
                                        Fielding
                                    </TableCell>
                                    {['GP', 'FP', 'TC', 'PO', 'A', 'E', 'DP'].map((col) => (
                                        <TableCell key={col} sx={compactHeaderCell}>
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...compactStatCell,
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {latest.year} {latest.team_level}
                                    </TableCell>
                                    {['GP', 'FP', 'TC', 'PO', 'A', 'E', 'DP'].map((col) => (
                                        <TableCell key={col} sx={compactStatCell}>
                                            {latest.fielding?.[col] || '-'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Scouting Notes 2x2 Grid */}
                <Divider sx={{ my: 1.5 }} />
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 700,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    Scouting Notes
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <TextField
                        label="Hitting"
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        value={playerNotes.hitting}
                        onChange={(e) => updateNote(profile.uuid, 'hitting', e.target.value)}
                        placeholder="Bat speed, approach, power, plate discipline, pitch recognition, swing mechanics..."
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontSize: '0.8rem',
                                '&::placeholder': { opacity: 0.18 },
                            },
                        }}
                    />
                    <TextField
                        label="Fielding"
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        value={playerNotes.fielding}
                        onChange={(e) => updateNote(profile.uuid, 'fielding', e.target.value)}
                        placeholder="Range, hands, arm strength, accuracy, positioning, double play turns..."
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontSize: '0.8rem',
                                '&::placeholder': { opacity: 0.18 },
                            },
                        }}
                    />
                    <TextField
                        label="Running"
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        value={playerNotes.running}
                        onChange={(e) => updateNote(profile.uuid, 'running', e.target.value)}
                        placeholder="Home to first time, first-step quickness, baserunning IQ, steal threat..."
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontSize: '0.8rem',
                                '&::placeholder': { opacity: 0.18 },
                            },
                        }}
                    />
                    <TextField
                        label="Pitching"
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        value={playerNotes.pitching}
                        onChange={(e) => updateNote(profile.uuid, 'pitching', e.target.value)}
                        placeholder="FB velocity/movement, secondary pitches, command, composure, arm action, deception..."
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontSize: '0.8rem',
                                '&::placeholder': { opacity: 0.18 },
                            },
                        }}
                    />
                </Box>
            </Paper>
        );
    };

    return (
        <Box className="scouting-print-root">
            {printStyles}

            {/* Toolbar (hidden on print) */}
            <Box
                className="scouting-no-print"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    pt: 2,
                    pb: 1,
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search players..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    sx={{ minWidth: 220 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                {filterText && (
                    <Chip
                        label={`Showing: "${filterText}"`}
                        size="small"
                        onDelete={() => setFilterText('')}
                        color="primary"
                        variant="outlined"
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => window.print()}
                >
                    Print Report
                </Button>
            </Box>

            <Box sx={{ px: 2, pb: 3 }}>
                {/* Report Header */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mt: 2,
                        mb: 1,
                    }}
                >
                    {groupLogo && (
                        <Avatar
                            src={groupLogo}
                            alt={groupName}
                            sx={{ width: 56, height: 56, mb: 1 }}
                        />
                    )}
                    {groupName && (
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {groupName}
                        </Typography>
                    )}
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Scouting Report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        })}
                        {' \u2022 '}
                        {profilesWithData.length} player
                        {profilesWithData.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>

                {/* Who's Hot Lately */}
                {(hotHitters.length > 0 || hotPitchers.length > 0) && (
                    <>
                        {sectionHeader(
                            "Who's Hot Lately",
                            '#d84315',
                            <FireIcon sx={{ color: '#d84315', fontSize: 20 }} />,
                        )}

                        {hotHitters.length > 0 && (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, ml: 1, textTransform: 'uppercase' }}
                                >
                                    Hot Hitters (last {RECENT_GAMES_COUNT} games)
                                </Typography>
                                <TableContainer
                                    sx={{
                                        mt: 0.5,
                                        mb: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                >
                                                    Player
                                                </TableCell>
                                                <TableCell sx={compactHeaderCell}>POS</TableCell>
                                                <TableCell sx={compactHeaderCell}>G</TableCell>
                                                <TableCell sx={compactHeaderCell}>AVG</TableCell>
                                                <TableCell sx={compactHeaderCell}>OBP</TableCell>
                                                <TableCell sx={compactHeaderCell}>SLG</TableCell>
                                                <TableCell sx={compactHeaderCell}>OPS</TableCell>
                                                <TableCell sx={compactHeaderCell}>SO</TableCell>
                                                <TableCell sx={compactHeaderCell}>BB</TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    Last Game
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {hotHitters.map((hh) => (
                                                <TableRow key={hh.profile.uuid} hover>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {getPlayerName(hh.profile)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {getPlayerBio(hh.profile).positions}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {hh.games}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...compactStatCell,
                                                            fontWeight: 700,
                                                            color: '#d84315',
                                                        }}
                                                    >
                                                        {formatStat3(hh.recentAvg)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {formatStat3(hh.recentObp)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {formatStat3(hh.recentSlg)}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...compactStatCell,
                                                            fontWeight: 700,
                                                            color: '#d84315',
                                                        }}
                                                    >
                                                        {formatStat3(hh.recentOps)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {getLatestSeason(hh.profile)?.batting?.K ??
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {getLatestSeason(hh.profile)?.batting?.BB ??
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {hh.sparkNote}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {hotPitchers.length > 0 && (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, ml: 1, textTransform: 'uppercase' }}
                                >
                                    Hot Pitchers (last {RECENT_GAMES_COUNT} appearances)
                                </Typography>
                                <TableContainer
                                    sx={{
                                        mt: 0.5,
                                        mb: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                >
                                                    Player
                                                </TableCell>
                                                <TableCell sx={compactHeaderCell}>G</TableCell>
                                                <TableCell sx={compactHeaderCell}>W-L</TableCell>
                                                <TableCell sx={compactHeaderCell}>ERA</TableCell>
                                                <TableCell sx={compactHeaderCell}>IP</TableCell>
                                                <TableCell sx={compactHeaderCell}>K</TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    Note
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {hotPitchers.map((hp) => (
                                                <TableRow key={hp.profile.uuid} hover>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {getPlayerName(hp.profile)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {hp.games}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {hp.recentW}-{hp.recentL}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...compactStatCell,
                                                            fontWeight: 700,
                                                            color: '#d84315',
                                                        }}
                                                    >
                                                        {hp.recentEra.toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {hp.recentIp.toFixed(1)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {hp.recentK}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {hp.sparkNote}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </>
                )}

                {/* Hitters to Watch */}
                {playersToWatch.filter((pw) => pw.keyStat.includes('OPS')).length > 0 && (
                    <>
                        {sectionHeader(
                            'Hitters to Watch',
                            '#1565c0',
                            <WatchIcon sx={{ color: '#1565c0', fontSize: 20 }} />,
                        )}
                        <TableContainer
                            sx={{
                                mb: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Player
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>Class</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                color: '#1565c0',
                                                fontWeight: 800,
                                            }}
                                        >
                                            AVG
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>AB</TableCell>
                                        <TableCell sx={compactHeaderCell}>K</TableCell>
                                        <TableCell sx={compactHeaderCell}>BB</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                fontWeight: 800,
                                            }}
                                        >
                                            K/(BB+HBP)
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>OBP</TableCell>
                                        <TableCell sx={compactHeaderCell}>SLG</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Why to Watch
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {playersToWatch
                                        .filter((pw) => pw.keyStat.includes('OPS'))
                                        .sort(
                                            (a, b) =>
                                                num(getLatestSeason(b.profile)?.batting?.AVG) -
                                                num(getLatestSeason(a.profile)?.batting?.AVG),
                                        )
                                        .map((pw) => {
                                            const bio = getPlayerBio(pw.profile);
                                            const bat = getLatestSeason(pw.profile)?.batting;
                                            const k = num(bat?.K);
                                            const bb = num(bat?.BB);
                                            const hbp = num(bat?.HBP);
                                            const kbbDenom = bb + hbp;
                                            const kbbRatio = kbbDenom > 0 ? k / kbbDenom : null;
                                            const kbbColor =
                                                kbbRatio === null
                                                    ? 'text.secondary'
                                                    : kbbRatio > 3.0
                                                    ? '#e65100'
                                                    : kbbRatio <= 1.5
                                                    ? '#2e7d32'
                                                    : 'inherit';
                                            return (
                                                <TableRow key={pw.profile.uuid} hover>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {getPlayerName(pw.profile)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bio.classYear}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...compactStatCell,
                                                            fontWeight: 800,
                                                            color: '#1565c0',
                                                        }}
                                                    >
                                                        {bat?.AVG ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bat?.AB ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bat?.K ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bat?.BB ?? '-'}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...compactStatCell,
                                                            fontWeight: 700,
                                                            color: kbbColor,
                                                        }}
                                                    >
                                                        {kbbRatio !== null
                                                            ? kbbRatio.toFixed(2)
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bat?.OBP ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bat?.SLG ?? '-'}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: '0.72rem',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        <Chip
                                                            label={
                                                                pw.type === 'breakout'
                                                                    ? 'Breakout'
                                                                    : 'Underclassman'
                                                            }
                                                            size="small"
                                                            color={
                                                                pw.type === 'breakout'
                                                                    ? 'warning'
                                                                    : 'info'
                                                            }
                                                            variant="outlined"
                                                            sx={{
                                                                mr: 1,
                                                                height: 20,
                                                                fontSize: '0.65rem',
                                                            }}
                                                        />
                                                        {pw.reason}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* Hitters Who Chase */}
                {hittersWhoChase.length > 0 && (
                    <>
                        {sectionHeader(
                            'Hitters Who Chase',
                            '#b71c1c',
                            <WatchIcon sx={{ color: '#b71c1c', fontSize: 20 }} />,
                        )}
                        <TableContainer
                            sx={{
                                mb: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Player
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>Class</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                color: '#1565c0',
                                                fontWeight: 800,
                                            }}
                                        >
                                            AVG
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>AB</TableCell>
                                        <TableCell sx={compactHeaderCell}>K</TableCell>
                                        <TableCell sx={compactHeaderCell}>BB</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                fontWeight: 800,
                                                color: '#b71c1c',
                                            }}
                                        >
                                            K/(BB+HBP)
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>OBP</TableCell>
                                        <TableCell sx={compactHeaderCell}>SLG</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Note
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {hittersWhoChase.map(({ profile, kbbRatio, bat }) => {
                                        const bio = getPlayerBio(profile);
                                        return (
                                            <TableRow key={profile.uuid} hover>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getPlayerName(profile)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bio.classYear}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 800,
                                                        color: '#1565c0',
                                                    }}
                                                >
                                                    {bat.AVG ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.AB ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.K ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.BB ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 700,
                                                        color: '#b71c1c',
                                                    }}
                                                >
                                                    {kbbRatio.toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.OBP ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.SLG ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {`${kbbRatio.toFixed(1)}x K rate`}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* Top Run Scorers */}
                {topRunScorers.length > 0 && (
                    <>
                        {sectionHeader(
                            'Top Run Scorers',
                            '#1b5e20',
                            <WatchIcon sx={{ color: '#1b5e20', fontSize: 20 }} />,
                        )}
                        <TableContainer
                            sx={{
                                mb: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Player
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>Class</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                color: '#1565c0',
                                                fontWeight: 800,
                                            }}
                                        >
                                            AVG
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>AB</TableCell>
                                        <TableCell sx={compactHeaderCell}>R</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                fontWeight: 800,
                                                color: '#1b5e20',
                                            }}
                                        >
                                            R/AB
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>OBP</TableCell>
                                        <TableCell sx={compactHeaderCell}>SLG</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topRunScorers.map(({ profile, rPerAb, bat }) => {
                                        const bio = getPlayerBio(profile);
                                        return (
                                            <TableRow key={profile.uuid} hover>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getPlayerName(profile)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bio.classYear}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 800,
                                                        color: '#1565c0',
                                                    }}
                                                >
                                                    {bat.AVG ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.AB ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.R ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 700,
                                                        color: '#1b5e20',
                                                    }}
                                                >
                                                    {rPerAb.toFixed(3)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.OBP ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.SLG ?? '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* Top Run Producers */}
                {topRunProducers.length > 0 && (
                    <>
                        {sectionHeader(
                            'Top Run Producers',
                            '#4a148c',
                            <WatchIcon sx={{ color: '#4a148c', fontSize: 20 }} />,
                        )}
                        <TableContainer
                            sx={{
                                mb: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Player
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>Class</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                color: '#1565c0',
                                                fontWeight: 800,
                                            }}
                                        >
                                            AVG
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>AB</TableCell>
                                        <TableCell sx={compactHeaderCell}>RBI</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                fontWeight: 800,
                                                color: '#4a148c',
                                            }}
                                        >
                                            RBI/AB
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>HR</TableCell>
                                        <TableCell sx={compactHeaderCell}>OBP</TableCell>
                                        <TableCell sx={compactHeaderCell}>SLG</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topRunProducers.map(({ profile, rbiPerAb, bat }) => {
                                        const bio = getPlayerBio(profile);
                                        return (
                                            <TableRow key={profile.uuid} hover>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getPlayerName(profile)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bio.classYear}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 800,
                                                        color: '#1565c0',
                                                    }}
                                                >
                                                    {bat.AVG ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.AB ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.RBI ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 700,
                                                        color: '#4a148c',
                                                    }}
                                                >
                                                    {rbiPerAb.toFixed(3)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.HR ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.OBP ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.SLG ?? '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* Runners */}
                {topRunners.length > 0 && (
                    <>
                        {sectionHeader(
                            'Runners',
                            '#e65100',
                            <WatchIcon sx={{ color: '#e65100', fontSize: 20 }} />,
                        )}
                        <TableContainer
                            sx={{
                                mb: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Player
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>Class</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                color: '#1565c0',
                                                fontWeight: 800,
                                            }}
                                        >
                                            AVG
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>BB</TableCell>
                                        <TableCell sx={compactHeaderCell}>K</TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                fontWeight: 800,
                                                color: '#e65100',
                                            }}
                                        >
                                            SB
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...compactHeaderCell,
                                                fontWeight: 700,
                                            }}
                                        >
                                            SBA
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topRunners.map(({ profile, sb, sba, bat }) => {
                                        const bio = getPlayerBio(profile);
                                        return (
                                            <TableRow key={profile.uuid} hover>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getPlayerName(profile)}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bio.classYear}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 800,
                                                        color: '#1565c0',
                                                    }}
                                                >
                                                    {bat.AVG ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.BB ?? '-'}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>
                                                    {bat.K ?? '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...compactStatCell,
                                                        fontWeight: 700,
                                                        color: '#e65100',
                                                    }}
                                                >
                                                    {sb}
                                                </TableCell>
                                                <TableCell sx={compactStatCell}>{sba}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* Pitchers to Watch */}
                {playersToWatch.filter((pw) => pw.keyStat.includes('ERA')).length > 0 && (
                    <>
                        {sectionHeader(
                            'Pitchers to Watch',
                            '#1565c0',
                            <WatchIcon sx={{ color: '#1565c0', fontSize: 20 }} />,
                        )}
                        <TableContainer
                            sx={{
                                mb: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Player
                                        </TableCell>
                                        <TableCell sx={compactHeaderCell}>Class</TableCell>
                                        <TableCell sx={compactHeaderCell}>ERA</TableCell>
                                        <TableCell sx={compactHeaderCell}>W-L</TableCell>
                                        <TableCell sx={compactHeaderCell}>IP</TableCell>
                                        <TableCell sx={compactHeaderCell}>K</TableCell>
                                        <TableCell sx={compactHeaderCell}>BB</TableCell>
                                        <TableCell sx={compactHeaderCell}>HBP</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                            Why to Watch
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {playersToWatch
                                        .filter((pw) => pw.keyStat.includes('ERA'))
                                        .map((pw) => {
                                            const bio = getPlayerBio(pw.profile);
                                            const pit = getLatestSeason(pw.profile)?.pitching;
                                            return (
                                                <TableRow key={pw.profile.uuid} hover>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {getPlayerName(pw.profile)}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {bio.classYear}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...compactStatCell,
                                                            fontWeight: 700,
                                                            color: '#1565c0',
                                                        }}
                                                    >
                                                        {pit?.ERA ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {pit ? `${pit.W ?? 0}-${pit.L ?? 0}` : '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {pit?.IP ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {pit?.K ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {pit?.BB ?? '-'}
                                                    </TableCell>
                                                    <TableCell sx={compactStatCell}>
                                                        {pit?.HBP ?? '-'}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: '0.72rem',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        <Chip
                                                            label={
                                                                pw.type === 'breakout'
                                                                    ? 'Breakout'
                                                                    : 'Underclassman'
                                                            }
                                                            size="small"
                                                            color={
                                                                pw.type === 'breakout'
                                                                    ? 'warning'
                                                                    : 'info'
                                                            }
                                                            variant="outlined"
                                                            sx={{
                                                                mr: 1,
                                                                height: 20,
                                                                fontSize: '0.65rem',
                                                            }}
                                                        />
                                                        {pw.reason}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* No hot / watch data fallback */}
                {hotHitters.length === 0 &&
                    hotPitchers.length === 0 &&
                    playersToWatch.filter((pw) => pw.keyStat.includes('ERA')).length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                            <Typography variant="body2">
                                Not enough game log data to auto-detect hot players or pitchers to
                                watch. Scouting notes are still available below for each player.
                            </Typography>
                        </Box>
                    )}

                {/* Player Scouting Cards */}
                <Box
                    sx={{
                        breakAfter: 'avoid',
                        '@media print': { breakAfter: 'avoid', pageBreakAfter: 'avoid' },
                    }}
                >
                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mb: 2,
                        }}
                    >
                        Individual Player Reports
                    </Typography>
                </Box>

                {orderedSections.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                            {filterText
                                ? 'No players match your search.'
                                : 'No player data available.'}
                        </Typography>
                    </Box>
                ) : (
                    orderedSections.map((section, idx) => (
                        <React.Fragment key={section.label || `section-${idx}`}>
                            {section.label && tierHeader(section.label)}
                            {section.profiles.map((profile) => renderPlayerCard(profile))}
                        </React.Fragment>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default BaseballScoutingReport;
